import { axiosClient } from "@/shared/api/axios-client";
import type {
  EntradaAlmacenAdmin,
  OrdenCompraAdmin,
  OrdenCompraInput,
  OrdenCompraResumen,
  Paginado,
} from "@/lib/types";

const BASE = "/api/admin/ordenes-compra";

export interface ListarOrdenesParams {
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

/** Listado admin PAGINADO server-side (todas las versiones · activa + históricas). */
export async function listarOrdenesCompra(
  params: ListarOrdenesParams = {}
): Promise<Paginado<OrdenCompraResumen>> {
  const { data } = await axiosClient.get<Paginado<OrdenCompraResumen>>(BASE, { params });
  return data;
}

export async function obtenerOrdenCompra(clave: string): Promise<OrdenCompraAdmin> {
  const { data } = await axiosClient.get<OrdenCompraAdmin>(`${BASE}/${encodeURIComponent(clave)}`);
  return data;
}

/** Detalle de una VERSIÓN de OC por su id (Guid) · la clave puede repetirse entre años. */
export async function obtenerOrdenCompraPorId(id: string): Promise<OrdenCompraAdmin> {
  const { data } = await axiosClient.get<OrdenCompraAdmin>(`${BASE}/id/${encodeURIComponent(id)}`);
  return data;
}

export async function crearOrdenCompra(
  clave: string,
  input: OrdenCompraInput
): Promise<OrdenCompraAdmin> {
  const { data } = await axiosClient.post<OrdenCompraAdmin>(BASE, { clave, input });
  return data;
}

export async function actualizarOrdenCompra(
  clave: string,
  input: OrdenCompraInput
): Promise<OrdenCompraAdmin> {
  const { data } = await axiosClient.put<OrdenCompraAdmin>(
    `${BASE}/${encodeURIComponent(clave)}`,
    input
  );
  return data;
}

/** Baja lógica (por id de versión) · conserva la OC para histórico, la saca del consumo. */
export async function darDeBajaOrdenCompra(id: string): Promise<void> {
  await axiosClient.delete(`${BASE}/id/${encodeURIComponent(id)}`);
}

/** Reactiva una versión de OC (por id) dada de baja. */
export async function reactivarOrdenCompra(id: string): Promise<void> {
  await axiosClient.post(`${BASE}/id/${encodeURIComponent(id)}/reactivar`);
}

/** Parsea el XML de la OC SIN guardarla · devuelve el detalle para previsualizar. */
export async function previewOrdenXml(file: File): Promise<OrdenCompraAdmin> {
  const form = new FormData();
  form.append("archivo", file);
  const { data } = await axiosClient.post<OrdenCompraAdmin>(`${BASE}/preview`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

/** Parsea + persiste la OC desde el XML (tras confirmar el preview). */
export async function importarOrdenXml(file: File): Promise<OrdenCompraAdmin> {
  const form = new FormData();
  form.append("archivo", file);
  const { data } = await axiosClient.post<OrdenCompraAdmin>(`${BASE}/importar`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

/**
 * Parsea + valida una entrada de almacén desde su XML (raíz ENTRADAMATERIAL) SIN
 * persistir · valida que sea parseable, con folio (del nombre ENTRADAS_{folio}.xml) y
 * que su clave de OC coincida con la OC dada. La entrada se agrega al editor de la OC.
 */
export async function previewEntradaXml(
  clave: string,
  file: File
): Promise<EntradaAlmacenAdmin> {
  const form = new FormData();
  form.append("archivo", file);
  const { data } = await axiosClient.post<EntradaAlmacenAdmin>(
    `${BASE}/${encodeURIComponent(clave)}/entradas/preview`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}
